/* eslint-disable no-template-curly-in-string */
import { decodeMacros } from './decodeMacros';
import { Dsp } from '../types/dsp';
import { PlayerState } from '../types/playlist';

export const dsps: Dsp[] = [
  {
    value: 'pixels',
    macros: {
      '{SA_REF_DOMAIN}': '${SOURCE}',
      '{SA_REF_PAGE}': '${PAGE}',
      '{SA_CAMPAIGN_NAME}': '${SSP_NAME}',
      '{SA_CAMPAIGN_ID}': '${CAMPAIGN_ID}',
      '{SA_CLICK_URL_ENC}': '${CLICK_URL_ENC}',
      '[timestamp]': '${CACHEBUSTER}',
    },
  },
  {
    value: 'dv360',
    macros: {
      '{SA_REF_DOMAIN}': '',
      '{SA_REF_PAGE}': '${SOURCE_URL_ENC}',
      '{SA_CAMPAIGN_NAME}': '',
      '{SA_CAMPAIGN_ID}': '${CAMPAIGN_ID}',
      '{SA_CLICK_URL_ENC}': '${CLICK_URL_ENC}',
      '[timestamp]': '${CACHEBUSTER}',
    }
  },
  {
    value: 'xandr',
    macros: {
      '{SA_REF_DOMAIN}': '',
      '{SA_REF_PAGE}': '${REFERER_URL_ENC}',
      '{SA_CAMPAIGN_NAME}': '',
      '{SA_CAMPAIGN_ID}': '${DEAL_ID}',
      '{SA_CLICK_URL_ENC}': '${CLICK_URL_ENC}',
      '[timestamp]': '${CACHEBUSTER}',
    }
  },
  {
    value: 'stackadapt',
    macros: {
      '{SA_REF_DOMAIN}': '{SA_REF_DOMAIN}',
      '{SA_REF_PAGE}': '{SA_REF_PAGE}',
      '{SA_CAMPAIGN_NAME}': '{SA_CAMPAIGN_NAME}',
      '{SA_CAMPAIGN_ID}': '{SA_CAMPAIGN_ID}',
      '{SA_CLICK_URL_ENC}': '{SA_CLICK_URL_ENC}',
      '[timestamp]': '[timestamp]',
    }
  },
  {
    value: 'thetradedesk',
    macros: {
      '{SA_REF_DOMAIN}': '%%TTD_SITE%%',
      '{SA_REF_PAGE}': '%%TTD_SITE_WITH_PATH%%',
      '{SA_CAMPAIGN_NAME}': '%%TTD_SUPPLYVENDOR%%',
      '{SA_CAMPAIGN_ID}': '%%TTD_CAMPAIGNID%%',
      '{SA_CLICK_URL_ENC}': '${CLICK_URL_ENC}',
      '[timestamp]': '',
    }
  },
];

export const previewUrl = (creative: PlayerState, organisation: string) => {
  let baseUrl = 'https://shorts.pixels.ai';
  if (process.env.REACT_APP_STAGE === 'local') {
    baseUrl = 'http://localhost:8080';
  } else if (process.env.REACT_APP_ASSET_STAGE === 'dev') {
    baseUrl = 'https://shorts-dev.pixels.ai';
  }
  const previewUrl = new URL(`${baseUrl}/build/${creative.player_id}/${creative.player_id === 'pixels-320-480-preroll' ? '320-480' : 'dmpu'}.html`);
  const params = {
    playlistId: creative.playlist_uuid,
    organisation,
    adUnit: creative.ad_unit,
    playerId: creative.player_id,
    videoSize: creative.video_size,
    clickUrl: creative.click_thru_url,
    enabledTray: creative.enabled_tray,
    clickThruOnTrayOnly: creative.click_thru_on_tray_only,
    trayColour: creative.tray_colour,
    trayImageFilename: creative.tray_image_filename,
    endSlateImageFilename: creative.end_slate_image_filename,
    lumenId: creative.lumen_id,
  };

  for (const [key, value] of Object.entries(params)) {
    if (!value) continue;
    previewUrl.searchParams.append(key, value.toString());
  }

  for (let i = 0; i < creative.impression_trackers.length; i++) {
    for (const [key, value] of Object.entries(creative.impression_trackers[i])) {
      previewUrl.searchParams.append(`impressionTrackers[${i}][${key}]`, value);
    }
  }

  for (let i = 0; i < creative.lumen_trackers.length; i++) {
    for (const [key, value] of Object.entries(creative.lumen_trackers[i])) {
      previewUrl.searchParams.append(`lumenTrackers[${i}][${key}]`, value);
    }
  }

  const creativeUrl = new URL(`https://pub${process.env.REACT_APP_STAGE === 'prod' ? '' : '-dev'}.pixels.ai/shorts-autoplay-loader-lib.js`);
  const creativeParams = {
    id: organisation,
    playerId: creative.player_id,
    playlistId: creative.playlist_uuid,

    // theme params
    videoSize: creative.video_size,
    enabledTray: creative.enabled_tray,
    clickThruOnTrayOnly: creative.click_thru_on_tray_only,
    trayColour: creative.tray_colour,
    trayImageFilename: creative.tray_image_filename,
    endSlateImageFilename: creative.end_slate_image_filename,

    // the macro below is the valid macro for StackAdapt, replace with relevant dsp
    domain_impression: '{SA_REF_DOMAIN}',
    page_impression: '{SA_REF_PAGE}',
    // it's possible there is no equivalent macro for this (campaign name), please
    // hardcoded the value, must be unique per campaign/deal
    ssp_name: '{SA_CAMPAIGN_NAME}',
    campaign_id: '{SA_CAMPAIGN_ID}',
    clickUrl: '{SA_CLICK_URL_ENC}',
    t: '[timestamp]',
  };

  for (const [key, value] of Object.entries(creativeParams)) {
    if (!value) continue;
    creativeUrl.searchParams.append(key, value.toString());
  }

  for (let i = 0; i < creative.impression_trackers.length; i++) {
    for (const [key, value] of Object.entries(creative.impression_trackers[i])) {
      creativeUrl.searchParams.append(`impression_tracker[${i}][${key}]`, value);
    }
  }

  for (let i = 0; i < creative.lumen_trackers.length; i++) {
    for (const [key, value] of Object.entries(creative.lumen_trackers[i])) {
      creativeUrl.searchParams.append(`lumen_trackers[${i}][${key}]`, value);
    }
  }

  return previewUrl.toString();
};

export const creativeUrl = (row: PlayerState, organisation: string) => {
  let baseUrl = 'https://shorts.pixels.ai';
  if (process.env.REACT_APP_STAGE === 'local') {
    baseUrl = 'http://localhost:8080';
  } else if (process.env.REACT_APP_ASSET_STAGE === 'dev') {
    baseUrl = 'https://shorts-dev.pixels.ai';
  }
  const previewUrl = new URL(`${baseUrl}/build/${row.player_id}/${row.player_id === 'pixels-320-480-preroll' ? '320-480' : 'dmpu'}.html`);

  const params = {
    playlistId: row.playlist_uuid,
    organisation,
    adUnit: row.ad_unit,
    playerId: row.player_id,
    videoSize: row.video_size,
    clickUrl: row.click_thru_url,
    enabledTray: row.enabled_tray,
    clickThruOnTrayOnly: row.click_thru_on_tray_only,
    trayColour: row.tray_colour,
    trayImageFilename: row.tray_image_filename,
    endSlateImageFilename: row.end_slate_image_filename,
    lumenId: row.lumen_id,
    creativeId: row.id
  };

  for (const [key, value] of Object.entries(params)) {
    if (!value) continue;
    previewUrl.searchParams.append(key, value.toString());
  }

  for (let i = 0; i < row.impression_trackers.length; i++) {
    for (const [key, value] of Object.entries(row.impression_trackers[i])) {
      previewUrl.searchParams.append(`impressionTrackers[${i}][${key}]`, value);
    }
  }

  for (let i = 0; i < row.lumen_trackers.length; i++) {
    for (const [key, value] of Object.entries(row.lumen_trackers[i])) {
      previewUrl.searchParams.append(`lumenTrackers[${i}][${key}]`, value);
    }
  }

  const creativeUrl = new URL(`https://pub${process.env.REACT_APP_STAGE === 'prod' ? '' : '-dev'}.pixels.ai/shorts-autoplay-loader-lib.js`);
  const creativeParams = {
    id: organisation,
    playerId: row.player_id,
    playlistId: row.playlist_uuid,
    lumenId: row.lumen_id,
    adUnit: row.ad_unit,
    creativeId: row.id,

    // theme params
    videoSize: row.video_size,
    enabledTray: row.enabled_tray,
    clickThruOnTrayOnly: row.click_thru_on_tray_only,
    trayColour: row.tray_colour,
    trayImageFilename: row.tray_image_filename,
    endSlateImageFilename: row.end_slate_image_filename,

    // the macro below is the valid macro for StackAdapt, replace with relevant dsp
    domain_impression: '{SA_REF_DOMAIN}',
    page_impression: '{SA_REF_PAGE}',
    // it's possible there is no equivalent macro for this (campaign name), please
    // hardcoded the value, must be unique per campaign/deal
    ssp_name: '{SA_CAMPAIGN_NAME}',
    campaign_id: '{SA_CAMPAIGN_ID}',
    clickUrl: '{SA_CLICK_URL_ENC}',
    t: '[timestamp]',
  };

  for (const [key, value] of Object.entries(creativeParams)) {
    if (!value) continue;
    creativeUrl.searchParams.append(key, value.toString());
  }

  for (let i = 0; i < row.impression_trackers.length; i++) {
    for (const [key, value] of Object.entries(row.impression_trackers[i])) {
      creativeUrl.searchParams.append(`impression_tracker[${i}][${key}]`, value);
    }
  }

  for (let i = 0; i < row.lumen_trackers.length; i++) {
    for (const [key, value] of Object.entries(row.lumen_trackers[i])) {
      creativeUrl.searchParams.append(`lumen_trackers[${i}][${key}]`, value);
    }
  }

  return creativeUrl.toString();
};

export const dspTag = (dsp: string, creativeUrl: string, clickThruURL: string) => {
  const selectedDsp = dsps.find(({ value }) => value === dsp);
  if (!selectedDsp) return;

  const macros = Object.entries(selectedDsp.macros);
  const initialTag = `<script src="${decodeMacros(creativeUrl)}"></script>`;
  const tag = macros.reduce((acc, [key, val]) => {
    return acc.replace(key, val);
  }, initialTag);

  // insert click thru url after the macro for all DSPs except stackadapt
  if (dsp !== 'stackadapt') {
    const selectedDsp = dsps.find(({ value }) => value === dsp);
    if (selectedDsp) {
      if (dsp === 'thetradedesk') {
        // For thetradedesk, replace the macro directly with the clickThruURL
        return tag.replace(selectedDsp.macros['{SA_CLICK_URL_ENC}'], decodeMacros(encodeURIComponent(clickThruURL)));
      } else {
        // For other DSPs, insert clickThruURL after the macro
        const [head, tail] = tag.split(selectedDsp.macros['{SA_CLICK_URL_ENC}']);
        return [
          head,
          selectedDsp.macros['{SA_CLICK_URL_ENC}'],
          decodeMacros(encodeURIComponent(clickThruURL)),
          tail
        ].join('');
      }
    }
  } else {
    return tag;
  }
};